import { useMemo } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

const markdownBodySx: SxProps<Theme> = {
  minWidth: 0,
  '& p': { m: 0 },
  '& strong': { fontWeight: 700 },
  '& ul, & ol': { pl: 2, m: 0 },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    my: 1,
    fontSize: '0.95em',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    py: 0.5,
    px: 0.75,
    textAlign: 'left',
    verticalAlign: 'top',
  },
  '& th': { fontWeight: 600, bgcolor: 'action.hover' },
};

/** Разрешаем `<br>` в санитайзере: он нужен для переносов внутри ячеек GFM-таблиц. */
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'br'],
};

/**
 * Нормализует сырой AI-текст до безопасного для GFM markdown.
 * Каждый шаг — один понятный тип «косяка» от модели/бэкенда.
 */
function normalizeAiContent(raw: string): string {
  if (!raw) return raw;

  let s = raw;

  // 1) Убираем BOM и zero-width символы — они ломают распознавание заголовков/таблиц.
  s = s.replace(/^\uFEFF/, '').replace(/[\u200B-\u200D\uFEFF]/g, '');

  // 2) CRLF → LF (Windows-концы).
  s = s.replace(/\r\n?/g, '\n');

  // 3) «Недораспаршенный JSON»: в строке нет реальных переводов, но есть литералы \n/\t.
  //    Безопасно раскрываем только в таком сценарии, чтобы не поломать обычный текст
  //    (где литерал `\n` мог быть написан юзером внутри code-блока).
  // Раньше разворачивали литералы только при отсутствии реальных переводов —
  // при смешанном вводе литералы оставались сырыми и ломали разметку.
  if (/\\[nrt]/.test(s)) {
    s = s.replace(/\\r\\n|\\n/g, '\n').replace(/\\t/g, '\t');
  }

  // 4) NBSP (\u00A0) и «narrow no-break space» (\u202F) оставляем как есть —
  //    это валидные unicode-пробелы, они корректно рендерятся в DOM.

  // 5) Обрезаем концевые пробелы строк-разделителей таблицы — некоторые модели
  //    добавляют трейлинг, из-за которого парсер иногда путается.
  //    (Необязательно, но дешёвая страховка.)
  s = s.replace(/[ \t]+$/gm, '');

  // 6) Unicode-буллеты → markdown-списки. `•` всегда буллет верхнего уровня.
  //    `–`/`—` после отступа = суб-буллет. Без отступа en/em-dash не трогаем
  //    (валидная пунктуация в обычном тексте).
  s = s.replace(/^(\s*)•[ \t]+/gm, '$1- ');
  s = s.replace(/^(\s{2,})[–—][ \t]+/gm, '$1- ');

  return s;
}

export function ChatMarkdown({ content }: { content: string }) {
  const prepared = useMemo(() => normalizeAiContent(content), [content]);

  return (
    <Box sx={markdownBodySx}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
      >
        {prepared}
      </ReactMarkdown>
    </Box>
  );
}
